import { isDevMode } from '@angular/core';
import { MinotaureInvestisseurFilterInput } from '@lib/models/generated/graphql';
import { OuiSelectOption } from 'omnium-ui/form-field';
import { Observable } from 'rxjs';

export type LineThemeCode =
  | 'BUTTON' //Button (filtres 'button' du side panel)
  | 'IMMO' // Immobilier
  | 'LEVI' //Leviers
  | 'FISC' // Fiscalité
  | 'PROFIL_INVESTISSEUR' // Profil investisseur
  | 'ACTIFS' // Actifs
  | 'CONTRATS_STELLIUM' // Contrats Stellium
  | 'PASSIFS' // Passifs
  | 'OBJECTIFS' // Objectifs
  | 'IDENTITE' // Identité
  | 'RGPD' // RGPD
  | 'PROFESSIONNEL' // Professionnel
  | 'RELATIONS' // Relations
  | 'REGLEMENTAIRE' // Réglementaire
  | 'REVENUS_CHARGES' // Revenus et charges
  | 'COORDONNEES' // Coordonnées
  | 'LEVIER_INVESTISSEUR'
  | 'LEVIER_COUPLE';

export type BuilderCode =
  // Etat Global
  | 'CONNAISSANCE_CLIENT_AVANCEMENT_GLOBAL'

  // Personnel et Professionnel (PERSOPRO)
  | 'PHYSIQUE_AGE'
  | 'ETAT_CIVIL_AVANTAGE_MATRIMONIAL'
  | 'PROFESSION_CSP'
  | 'PROFESSION_DATE_DEPART_RETRAITE'
  | 'DISPOSITION_DONATION_PROCHES'
  | 'DISPOSITION_DONATION_DERNIER_VIVANT'
  | 'ETAT_CIVIL_ENFANTS_CHARGE'
  | 'REGLEMENTAIRE_EXPOSE_POLITIQUEMENT'
  | 'ETAT_CIVIL_REGIME_MATRIMONIAL'
  | 'ETAT_CIVIL_SITUATION_MATRIMONIALE'
  | 'PHYSIQUE_ADRESSE_MODE_LOGEMENT'

  // Patrimoine (PATR)
  | 'PATRIMOINE_IMMOBILIER_MODE_DETENTION'
  | 'PATRIMOINE_IMMOBILIER_DATE_ACQUISITION'

  // Fiscalité (FISC)
  | 'FISCAL_IFI_NET'
  | 'FISCAL_IMPOSABLE_IFI'
  | 'FISCAL_IMPOT_BRUT'
  | 'FISCAL_IR_NET'
  | 'FISCAL_PARTS_FISCALES_NOMBRE'
  | 'FISCAL_TAUX_MARGINAL_IMPOSITION'
  | 'FISCAL_TOTAL_REDUCTION'
  | 'FISCAL_IMPOT_TOTAL_AVANT_CREDITS'
  | 'FISCAL_PLAFOND_EPARGNE_RETRAITE_NON_UTILISE'
  | 'FISCAL_REVENU_BRUT_GLOBAL'
  | 'FISCAL_TOTAL_CREDITS_IMPUTATIONS'

  //ACTIFS
  | 'PATRIMOINE_ACTIF_TYPE'
  | 'PATRIMOINE_ACTIFS_TOTAL_VALEUR'

  // Contrats Stellium
  | 'CONTRATS_STELLIUM_VP_MONTANT_MENSUEL'
  | 'CONTRATS_STELLIUM_MONTANT_INVESTI'
  | 'CONTRATS_STELLIUM_VERSEMENTS_PROGRAMMES'
  | 'CONTRATS_STELLIUM_PERFORMANCE_FINANCIERE'
  | 'CONTRATS_STELLIUM_VALORISATION'

  //Passifs
  | 'PATRIMOINE_PASSIFS_CAPITAL_RESTANT_DU'
  | 'PATRIMOINE_PASSIFS_EN_COURS'
  | 'PATRIMOINE_PASSIFS_PROCHAINE_ECHEANCE'

  // Revenus et charges
  | 'REVENU_TOTAL_EFFECTIF'
  | 'CHARGES_TOTAL_EFFECTIF'

  // Profil investisseur
  | 'QPI_CONNAISSANCE'
  | 'QPI_TOLERANCE_PERTE'
  | 'QPI_PROFIL_RISQUE_INV'
  | 'QPI_ESG'

  //Objectifs
  | 'OBJECTIFS_INV'

  //RGPD
  | 'RGPD_DATE_SUPPRESSION'

  //MyStellium
  | 'MYSTELLIUM_STATUS'

  // Button
  | 'INVESTISSEUR_ORIGINE_CONTACT'
  | 'INVESTISSEUR_STATUT'
  | 'INVESTISSEUR_ETIQUETTE'
  | 'REC_STATUT'
  | 'QPI_STATUT'
  | 'DER_STATUT'
  | 'PIECEIDENTITE_STATUT'
  | 'RELATION_SECONDAIRE'

  //LEVIER _INVESTISSEUR
  | 'LEVIER_INV_CAPACITE_REMBOURSEMENT_EMPRUNT_RESTANT_MENSUELLE'
  | 'LEVIER_INV_CAPACITE_EPARGNE_MENSUELLE'
  | 'LEVIER_INV_EFFORT_EPARGNE_MENSUEL'
  | 'LEVIER_INV_EPARGNE_PRECAUTION_REELLE'
  | 'LEVIER_INV_EPARGNE_PRECAUTION_SOUHAITEE'
  | 'LEVIER_INV_EPARGNE_DISPONIBLE'
  | 'LEVIER_INV_MENSUALITE_CREDIT_TOTALE'
  | 'LEVIER_INV_TAUX_ENDETTEMENT'

  //LEVIER_COUPLE
  | 'LEVIER_COUPLE_CAPACITE_REMBOURSEMENT_EMPRUNT_RESTANT_MENSUELLE'
  | 'LEVIER_COUPLE_CAPACITE_EPARGNE_MENSUELLE'
  | 'LEVIER_COUPLE_EFFORT_EPARGNE_MENSUEL'
  | 'LEVIER_COUPLE_EPARGNE_PRECAUTION_REELLE'
  | 'LEVIER_COUPLE_EPARGNE_PRECAUTION_SOUHAITEE'
  | 'LEVIER_COUPLE_EPARGNE_DISPONIBLE'
  | 'LEVIER_COUPLE_MENSUALITE_CREDIT_TOTALE'
  | 'LEVIER_COUPLE_TAUX_ENDETTEMENT';

export type LineOperator = 'eq' | 'gt' | 'gte' | 'lt' | 'lte' | 'in' | 'nin';
export type LineInputType = 'Select' | 'InputNumeric' | 'SelectMultiple' | 'Date' | 'InputEuro' | 'InputPercent' | 'Age' | 'DateTime';

export abstract class FiltreBuilder {
  protected themeCode: LineThemeCode;
  protected code: BuilderCode;
  protected filterPath: string;
  public options?: Observable<OuiSelectOption[] | null>;
  public inputType: LineInputType;
  public operatorOptions: OuiSelectOption<LineOperator>[];
  public operator?: LineOperator;
  public readonly isButton: boolean;
  public maxInputLength?: number;
  public customPlaceholder?: string;

  constructor(
    themeCode: LineThemeCode,
    code: BuilderCode,
    filterPath: string,
    operators: LineOperator[],
    inputType: LineInputType,
    valueOptions?: Observable<OuiSelectOption[] | null>,
    maxInputLength?: number | undefined
  ) {
    this.themeCode = themeCode;
    this.code = code;
    this.filterPath = filterPath;
    this.options = valueOptions;
    this.inputType = inputType;
    this.operatorOptions = operators.map(operator => {
      return {
        label: this.getOperatorLibelle(operator),
        value: operator,
      };
    });
    this.isButton = themeCode === 'BUTTON';
    if (maxInputLength) {
      this.maxInputLength = maxInputLength;
      const maxValueString = '9'.repeat(maxInputLength);
      this.customPlaceholder = `entre 0 et ${maxValueString}`;
    }
  }
  abstract buildFilter(operator: LineOperator, value: any): MinotaureInvestisseurFilterInput;
  getThemeCode(): LineThemeCode {
    return this.themeCode;
  }
  getBuilderCode(): BuilderCode {
    return this.code;
  }

  getOperatorLibelle(operator: LineOperator, forChips = false): string {
    const operatorMap: Record<LineOperator, string> = {
      eq: 'égal à',
      gt: 'plus grand que',
      gte: 'plus grand ou égal à',
      lt: 'plus petit que',
      lte: 'plus petit ou égal à',
      in: 'est égal',
      nin: "n'est pas égal",
    };
    const chipsOperatorMap: Record<LineOperator, string> = {
      eq: ':',
      gt: 'plus grand que',
      gte: 'plus grand ou égal à',
      lt: 'plus petit que',
      lte: 'plus petit ou égal à',
      in: ':',
      nin: 'pas égal',
    };
    if (forChips) {
      return chipsOperatorMap[operator];
    }
    return operatorMap[operator];
  }

  static getBuilderLibelle(code: BuilderCode): string {
    const itemMap: Record<BuilderCode, string> = {
      //ACTIFS
      PATRIMOINE_ACTIF_TYPE: 'Type d’actif',
      PATRIMOINE_ACTIFS_TOTAL_VALEUR: 'Total actifs - valeur',

      // Contrats Stellium
      CONTRATS_STELLIUM_VP_MONTANT_MENSUEL: 'Versements programmés - montant mensuel',
      CONTRATS_STELLIUM_MONTANT_INVESTI: 'Montant investi',
      CONTRATS_STELLIUM_VERSEMENTS_PROGRAMMES: 'Versements programmés - présence',
      CONTRATS_STELLIUM_PERFORMANCE_FINANCIERE: 'Contrats - perf. fin. globale en %',
      CONTRATS_STELLIUM_VALORISATION: 'Valorisation',

      // Passifs
      PATRIMOINE_PASSIFS_CAPITAL_RESTANT_DU: 'Passifs - Capital restant dû (total)',
      PATRIMOINE_PASSIFS_EN_COURS: 'Crédits en cours',
      PATRIMOINE_PASSIFS_PROCHAINE_ECHEANCE: 'Date prochaine fin de crédit',

      // Revenus et charges
      REVENU_TOTAL_EFFECTIF: 'Total revenus - valeur',
      CHARGES_TOTAL_EFFECTIF: 'Total charges - valeur',

      // Recueil (REC)
      CONNAISSANCE_CLIENT_AVANCEMENT_GLOBAL: 'Connaissance client',
      REC_STATUT: 'Recueil d’informations',
      // Question Profil Investisseur (QPI)
      QPI_STATUT: 'Profil Investisseur',
      QPI_CONNAISSANCE: 'Connaissance et expérience',
      QPI_TOLERANCE_PERTE: 'Capacité à supporter des pertes',
      QPI_PROFIL_RISQUE_INV: 'Profil de risque',
      QPI_ESG: 'Sensibilité extra-financière',

      // Personnel et Professionnel (PERSOPRO)
      PHYSIQUE_AGE: 'Âge',
      ETAT_CIVIL_AVANTAGE_MATRIMONIAL: 'Avantage matrimonial',
      PROFESSION_CSP: 'Catégorie socio-professionnelle',
      PROFESSION_DATE_DEPART_RETRAITE: 'Date de départ à la retraite envisagée',
      DISPOSITION_DONATION_PROCHES: 'Donation à vos proches',
      DISPOSITION_DONATION_DERNIER_VIVANT: 'Donation au dernier vivant',
      ETAT_CIVIL_ENFANTS_CHARGE: 'Enfants à charge',
      REGLEMENTAIRE_EXPOSE_POLITIQUEMENT: 'Exposé politiquement',
      ETAT_CIVIL_REGIME_MATRIMONIAL: 'Régime matrimonial',
      ETAT_CIVIL_SITUATION_MATRIMONIALE: 'Situation matrimoniale',
      PHYSIQUE_ADRESSE_MODE_LOGEMENT: 'Statut de résidence',

      // Patrimoine (PATR)

      PATRIMOINE_IMMOBILIER_MODE_DETENTION: 'Actifs immobilier - Mode de détention',
      PATRIMOINE_IMMOBILIER_DATE_ACQUISITION: 'Actifs immobilier - Date d’acquisition',

      // Fiscalité (FISC)
      FISCAL_IFI_NET: 'IFI net à payer',
      FISCAL_IMPOSABLE_IFI: 'Imposable à l’impôt sur la fortune immobilière (IFI) ?',
      FISCAL_IMPOT_BRUT: 'Impôt brut (avant réduction et crédit d’impôt)',
      FISCAL_IR_NET: 'IR net à payer',
      FISCAL_PARTS_FISCALES_NOMBRE: 'Parts fiscales - Nombre',
      FISCAL_TAUX_MARGINAL_IMPOSITION: 'Taux Marginal d’Imposition (TMI)',
      FISCAL_TOTAL_REDUCTION: 'Total des réductions d’impôts',
      FISCAL_IMPOT_TOTAL_AVANT_CREDITS: 'Impôt total avant crédits d’impôt',
      FISCAL_PLAFOND_EPARGNE_RETRAITE_NON_UTILISE: 'Plafond épargne retraite non utilisé',
      FISCAL_REVENU_BRUT_GLOBAL: 'Revenu brut global',
      FISCAL_TOTAL_CREDITS_IMPUTATIONS: 'Total des crédits d’impôt, imputations',

      DER_STATUT: "Document d'Entrée en Relation",
      PIECEIDENTITE_STATUT: "Pièce d'Identité",
      RELATION_SECONDAIRE: 'Relation secondaire',
      INVESTISSEUR_STATUT: 'Statut',
      INVESTISSEUR_ETIQUETTE: 'Étiquettes',
      INVESTISSEUR_ORIGINE_CONTACT: 'Origine du contact',

      //RGPD
      RGPD_DATE_SUPPRESSION: 'Date de purge',

      //MyStellium
      MYSTELLIUM_STATUS: 'Statut du compte MyStellium',

      // Objectifs
      OBJECTIFS_INV: 'Objectifs (individuels ou communs)',

      // Leviers
      LEVIER_INV_CAPACITE_EPARGNE_MENSUELLE: 'Capacité d’épargne mensuelle (individuel)',
      LEVIER_INV_EFFORT_EPARGNE_MENSUEL: 'Effort d’épargne mensuel (individuel)',
      LEVIER_INV_EPARGNE_PRECAUTION_REELLE: 'Épargne de précaution réelle (individuel)',
      LEVIER_INV_EPARGNE_PRECAUTION_SOUHAITEE: 'Épargne de précaution souhaitée (individuel)',
      LEVIER_INV_MENSUALITE_CREDIT_TOTALE: "Montant mensuel consacré au remboursement d'emprunts (individuel) ",
      LEVIER_INV_TAUX_ENDETTEMENT: 'Taux d’endettement (individuel)',
      LEVIER_INV_CAPACITE_REMBOURSEMENT_EMPRUNT_RESTANT_MENSUELLE: "Capacité mensuelle de remboursement d'emprunt restante (individuel)",
      LEVIER_INV_EPARGNE_DISPONIBLE: 'Épargne disponible (individuel)',

      LEVIER_COUPLE_CAPACITE_EPARGNE_MENSUELLE: 'Capacité d’épargne mensuelle (couple)',
      LEVIER_COUPLE_EFFORT_EPARGNE_MENSUEL: 'Effort d’épargne mensuel (couple)',
      LEVIER_COUPLE_EPARGNE_PRECAUTION_REELLE: 'Épargne de précaution réelle (couple)',
      LEVIER_COUPLE_EPARGNE_PRECAUTION_SOUHAITEE: 'Épargne de précaution souhaitée (couple)',
      LEVIER_COUPLE_MENSUALITE_CREDIT_TOTALE: "Montant mensuel consacré au remboursement d'emprunts (couple)",
      LEVIER_COUPLE_TAUX_ENDETTEMENT: 'Taux d’endettement (couple)',
      LEVIER_COUPLE_CAPACITE_REMBOURSEMENT_EMPRUNT_RESTANT_MENSUELLE: "Capacité mensuelle de remboursement d'emprunt restante (couple)",
      LEVIER_COUPLE_EPARGNE_DISPONIBLE: 'Épargne disponible (couple)',
    };
    return itemMap[code];
  }

  static getThemeLibelle(code: LineThemeCode): string {
    const themeMap: Record<LineThemeCode, string> = {
      IMMO: 'Immobilier',
      LEVI: 'Leviers',
      FISC: 'Fiscalité',
      BUTTON: '',
      PROFIL_INVESTISSEUR: 'Profil investisseur',
      ACTIFS: 'Actifs',
      CONTRATS_STELLIUM: 'Contrats Stellium',
      PASSIFS: 'Passifs',
      OBJECTIFS: 'Objectifs',
      PROFESSIONNEL: 'Professionnel',
      IDENTITE: 'Identité',
      COORDONNEES: 'Coordonnées',
      RGPD: 'RGPD',
      RELATIONS: 'Relations',
      REGLEMENTAIRE: 'Réglementaire',
      REVENUS_CHARGES: 'Revenus et charges',
      LEVIER_INVESTISSEUR: 'Leviers (individuel)',
      LEVIER_COUPLE: 'Leviers (couple)',
    };
    return themeMap[code];
  }

  /**
   * Transforme un chemin "a.b.c" et une valeur { eq: 5 }
   * en un objet imbriqué { a: { b: { c: { eq: 5 } } } }
   */
  protected buildNestedObject(path: string, finalLeafValue: any): any {
    const pathParts = path.split('.');
    const result: any = {};
    let current = result;

    for (let i = 0; i < pathParts.length; i++) {
      const key = pathParts[i];
      if (i === pathParts.length - 1) {
        current[key] = finalLeafValue;
      } else {
        current[key] = {};
        current = current[key];
      }
    }
    return result;
  }
}

export class ItemFiltreCustomBuilder extends FiltreBuilder {
  override buildFilter(operator: LineOperator, value: unknown): MinotaureInvestisseurFilterInput {
    const filterValue = Array.isArray(value) ? value.flat() : value;

    return this.buildNestedObject(this.filterPath, { [operator]: filterValue }) as MinotaureInvestisseurFilterInput;
  }
}

export class CompositeFiltreCustomBuilder extends FiltreBuilder {
  private staticConditions: { fieldPath: string; operator: LineOperator; value: any }[] = [];
  private dynamicPath: string;

  constructor(
    themeCode: LineThemeCode,
    itemCode: BuilderCode,
    filterPath: string,
    dynamicPath: string,
    operators: LineOperator[],
    inputType: LineInputType,
    valueOptions?: Observable<OuiSelectOption[] | null>,
    staticConditions?: { fieldPath: string; operator: LineOperator; value: any }[],
    maxInputLength?: number | undefined
  ) {
    super(themeCode, itemCode, filterPath, operators, inputType, valueOptions, maxInputLength);
    this.staticConditions = staticConditions ?? [];
    this.dynamicPath = dynamicPath;
  }

  override buildFilter(operator: LineOperator, value: unknown): MinotaureInvestisseurFilterInput {
    const allConditions = [
      ...this.staticConditions.map(c => this.buildNestedObject(c.fieldPath, { [c.operator]: c.value })),
      this.buildNestedObject(this.dynamicPath, { [operator]: value }),
    ];

    return this.buildNestedObject(this.filterPath, allConditions) as MinotaureInvestisseurFilterInput;
  }
}

export class AgeFiltreCustomBuilder extends FiltreBuilder {
  constructor(
    themeCode: LineThemeCode,
    itemCode: BuilderCode,
    filterPath: string, // ex: "personnePhysique.some.dateNaissance"
    operators: LineOperator[],
    inputType: LineInputType,
    valueOptions?: Observable<OuiSelectOption[] | null>,
    maxInputLength?: number | undefined
  ) {
    super(themeCode, itemCode, filterPath, operators, inputType, valueOptions, maxInputLength);
  }

  private formatDateLocal(date: Date): string {
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - offset * 60 * 1000);
    return localDate.toISOString().split('T')[0];
  }

  private ageToBirthdate(age: number): string {
    const birthdate = new Date();
    birthdate.setFullYear(birthdate.getFullYear() - age);
    return this.formatDateLocal(birthdate);
  }

  private translateOperator(operator: LineOperator): { op: LineOperator; isRange?: boolean } {
    switch (operator) {
      case 'gt':
        return { op: 'lt' };
      case 'gte':
        return { op: 'lte' };
      case 'lt':
        return { op: 'gt' };
      case 'lte':
        return { op: 'gte' };
      case 'eq':
        return { op: 'eq', isRange: true }; // Signal pour gérer une plage
      default:
        throw new Error(`Opérateur non supporté pour les filtres d'âge: ${operator}`);
    }
  }

  override buildFilter(operator: LineOperator, value: any): MinotaureInvestisseurFilterInput {
    if (isNaN(Number(value))) {
      if (isDevMode()) {
        console.error(`Valeur d'âge invalide - not a number: ${value}`);
      }
      value = '0';
    }

    let age = parseInt(value);

    if (age < 0) {
      if (isDevMode()) {
        console.error(`Valeur d'âge  - negative value: ${value}`);
      }
      // force positive value
      age = Math.abs(age);
    }
    const { op: dateOperator, isRange } = this.translateOperator(operator);
    const pathParts = this.filterPath.split('.');
    const lastKey = pathParts[pathParts.length - 1];
    const current: any = {};
    let temp = current;

    for (let i = 0; i < pathParts.length - 1; i++) {
      const key = pathParts[i];
      temp[key] = {};
      temp = temp[key];
    }

    // case 'eq' (date range : gte et lte dans "and")
    // pour l'age précis on doit borner la date de naissance
    if (isRange) {
      const today = new Date();
      const year = today.getFullYear() - age;
      const startDate = new Date(year - 1, today.getMonth(), today.getDate() + 1);
      const endDate = new Date(year, today.getMonth(), today.getDate());

      temp.and = [{ [lastKey]: { gte: this.formatDateLocal(startDate) } }, { [lastKey]: { lte: this.formatDateLocal(endDate) } }];
    } else {
      // other cases
      const birthdate = this.ageToBirthdate(age);
      temp[pathParts[pathParts.length - 1]] = { [dateOperator]: birthdate };
    }

    return current as MinotaureInvestisseurFilterInput;
  }
}
